import React, {useEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import {AddOutlined, DeleteOutlined} from '@material-ui/icons';
import PageWrapper from '../../../shared/pagewrapper/PageWrapper.js';
import PageTitle from '../../../shared/pagetitle/PageTitle.js';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import BlockUI from '../../../shared/blockui/BlockUI.tsx';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import {useToast} from '../../../shared/toast/ToastProvider.tsx';
import {criarColunaOpcoes} from '../../components/ColunaOpcoes.jsx';
import DialogSelecionarFuncionario from '../../dialogs/cadastro/DialogSelecionarFuncionario.jsx';
import FotoPessoa from '../../components/cadastro/FotoPessoa.jsx';
import {
    formatarDataIso,
    MENSAGEM_RESPONSAVEL_ADICIONADO,
    MENSAGEM_RESPONSAVEL_EXCLUIDO,
    montarPayloadResponsavel
} from '../../components/cadastro/cadastroAtendimento.js';
import {ouTraco} from '../../components/inscricao/documentoInscricao.js';
import PageNaoEncontrada from '../../../PageNaoEncontrada.jsx';
import useCheckModule from '../../../../hooks/useCheckModule.js';
import useDialogState from '../../../../hooks/useDialogState.js';
import useFetch from '../../../../hooks/useFetch.js';
import useCreate from '../../../../hooks/useCreate.js';
import useRemove from '../../../../hooks/useRemove.js';
import {URL_RESPONSAVEIS} from '../../../../constants/index.js';
import {buttonTableCell, containerPaper} from '../../../../utils/styles.js';
import {PAGINACAO_PADRAO, textoSemRegistros} from '../../components/paginacao.js';

const PARAMS_VAZIOS = {};
const FILTER_OPTIONS = {filterByColumn: true};
const TABLE_CONTAINER_PROPS = {component: 'div'};

const useStyles = makeStyles(theme => ({
    ...containerPaper(theme),
    colunaFoto: {
        width: theme.spacing(9)
    },
    colunaData: {
        width: theme.spacing(18)
    },
    ...buttonTableCell(theme)
}));

/*
 * Responsáveis (módulo CFRESPONS): lista de funcionários responsáveis. "Adicionar responsável"
 * abre a busca de funcionários, que fica aberta: cada "Adicionar" grava e recarrega a lista.
 * Os erros do back (por exemplo, responsável duplicado) já são mostrados pelos hooks.
 */
export default function PageResponsaveis() {
    const classes = useStyles();
    const checkModule = useCheckModule();
    const podeAcessar = checkModule('CFRESPONS');
    const [fetch, carregando] = useFetch({returnInnerLoadingState: true});
    const [create, criando] = useCreate({returnInnerLoadingState: true});
    const [remove, removendo] = useRemove({returnInnerLoadingState: true});
    const confirmation = useConfirmationDialog();
    const {createToast} = useToast();
    const {dialogState, openDialog, closeDialog} = useDialogState({funcionarios: false});
    const [itens, setItens] = useState([]);

    // A credencial renova a cada resposta: os callbacks chamam sempre o fetch do render mais recente.
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;

    const carregar = () => fetchRef.current(URL_RESPONSAVEIS, PARAMS_VAZIOS, dados => setItens(Array.isArray(dados) ? dados : []));

    useEffect(() => {
        if (podeAcessar) {
            carregar();
        }
    }, [podeAcessar]);

    const adicionar = funcionario => create(URL_RESPONSAVEIS, montarPayloadResponsavel(funcionario.idPessoa), () => {
        createToast({severity: 'success', message: MENSAGEM_RESPONSAVEL_ADICIONADO});
        carregar();
    });

    const excluir = responsavel => {
        confirmation('Tem certeza que deseja excluir?', () =>
            remove(`${URL_RESPONSAVEIS}/${responsavel.id}`, () => {
                createToast({severity: 'success', message: MENSAGEM_RESPONSAVEL_EXCLUIDO});
                carregar();
            }));
    };

    if (!podeAcessar) {
        return <PageNaoEncontrada/>;
    }

    return (
        <PageWrapper>
            <PageTitle title='Responsáveis'/>
            <Paper className={classes.containerPaper}>
                <BlockUI loading={carregando || criando || removendo}>
                    <DataGrid
                        rows={itens}
                        comparatorKeys={['id']}
                        filterOptions={FILTER_OPTIONS}
                        paginate
                        paginateOptions={PAGINACAO_PADRAO}
                        tableContainerProps={TABLE_CONTAINER_PROPS}
                        noRecordsText={textoSemRegistros(carregando)}
                        toolbarContent={(
                            <ContainedButton startIcon={<AddOutlined/>} onClick={openDialog('funcionarios')}>
                                Adicionar responsável
                            </ContainedButton>
                        )}
                        columns={[
                            {
                                label: 'Foto',
                                filterable: false,
                                className: classes.colunaFoto,
                                valueGetter: (valor, row) => <FotoPessoa urlFoto={row.urlFoto} nome={row.nome}/>
                            },
                            {label: 'Responsável', field: 'nome', filterable: true, valueGetter: valor => ouTraco(valor)},
                            {
                                label: 'Data de inserção',
                                field: 'dataInsercao',
                                filterable: false,
                                className: classes.colunaData,
                                valueGetter: valor => ouTraco(formatarDataIso(valor))
                            },
                            {label: 'Situação', field: 'situacao', filterable: true, valueGetter: valor => ouTraco(valor)},
                            criarColunaOpcoes(row => [
                                {titulo: 'Excluir', icone: <DeleteOutlined/>, onClick: () => excluir(row)}
                            ], {className: classes.buttonTableCell})
                        ]}/>
                </BlockUI>
            </Paper>
            <DialogSelecionarFuncionario
                open={dialogState.funcionarios}
                onClose={closeDialog('funcionarios')}
                onSelecionar={adicionar}
                manterAberto
                titulo='Selecionar funcionário'
                tituloAcao='Adicionar'
                ocupado={criando}/>
        </PageWrapper>
    );
}
